import React, { useState } from 'react';
import { Container } from '../components/Container';
import { ChevronDown, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const PROCESS_STEPS = [{
  title: 'How to Start',
  content: "Begin by contacting us with your team's needs, sport, and timeline. If you have a quote from another company, send it over and we will beat it. You can start by placing an Art Deposit to get mockups going, or a Sample Deposit to see physical quality first."
}, {
  title: 'Artwork Approval',
  content: 'Our art team will work with you to create custom mockups based on your colors, logos, and ideas. We offer unlimited revisions until you are 100% satisfied with the design. Nothing moves forward without your explicit approval.'
}, {
  title: 'Order Forms / Sizes / Names / Numbers',
  content: 'Once art is approved, we will provide you with an organized order form. This is where you will list all player sizes, names, and numbers. We double-check this form with you to ensure complete accuracy before production.'
}, {
  title: 'Deposit / Production',
  content: 'With art and sizes finalized, a 50% deposit is required to officially begin production. Once the deposit is received, your order is scheduled in our factory.'
}, {
  title: 'Mini Photo Approval',
  content: "Before printing the full order, we print a 'mini' version of your uniform on fabric and send you a photo. This ensures the colors and logos look exactly as expected when transferred to the actual material."
}, {
  title: 'Full Size Sample Photo Approval',
  content: "After the mini approval, we produce one complete, full-size uniform and send you detailed photos. This is your final check to ensure the placement, sizing, and overall look are perfect before we run the rest of the team's gear."
}, {
  title: 'Final Production',
  content: 'Once the full-size sample is approved, we complete the production of the entire order. Our factory direct control means we monitor quality at every step.'
}, {
  title: 'Shipping',
  content: 'When production is complete, the final balance is due. Once paid, your order is shipped directly to you. We provide tracking information so you know exactly when your gear will arrive.'
}, {
  title: 'Rush Orders / Planning Ahead',
  content: 'Standard turnaround is typically 3-4 weeks after final approval and deposit. If you need gear faster, ask about our Rush Order options. We highly recommend our Early Bird program to lock in pricing and avoid pre-season stress.'
}];
export function Process() {
  const [openStep, setOpenStep] = useState<number | null>(0);
  const toggleStep = (index: number) => {
    setOpenStep(openStep === index ? null : index);
  };
  return <div className="w-full bg-gray-50 min-h-screen pb-20" data-id="element-526">
      {/* Header */}
      <div className="bg-[#111827] text-white py-16" data-id="element-527">
        <Container data-id="element-528">
          <div className="max-w-3xl" data-id="element-529">
            <h1 className="font-oswald text-4xl md:text-5xl font-bold uppercase mb-4" data-id="element-530">
              Our Ordering Process
            </h1>
            <div className="w-20 h-1.5 bg-pts-red mb-6" data-id="element-531"></div>
            <p className="text-xl text-gray-300" data-id="element-532">
              We've built a step-by-step system designed to eliminate mistakes,
              ensure quality, and take the stress out of ordering custom
              uniforms.
            </p>
          </div>
        </Container>
      </div>

      <Container className="py-16" data-id="element-533">
        <div className="max-w-4xl mx-auto" data-id="element-534">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-12" data-id="element-535">
            <h3 className="font-oswald text-2xl font-bold uppercase text-gray-900 mb-4 flex items-center" data-id="element-536">
              <CheckCircle2 className="h-6 w-6 text-pts-red mr-3" data-id="element-537" />
              Built for Accuracy
            </h3>
            <p className="text-gray-600" data-id="element-538">
              Unlike automated online builders that leave you hoping the final
              product looks right, our process includes human support, mini
              photo approvals, and full-size sample approvals. You see exactly
              what you're getting before the full order is produced.
            </p>
          </div>

          <div className="space-y-4 relative" data-id="element-539">
            {/* Vertical Line */}
            <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gray-200 hidden md:block" data-id="element-540"></div>

            {PROCESS_STEPS.map((step, index) => {
            const isOpen = openStep === index;
            return <div key={index} className="relative bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden z-10" data-id="element-541">
                  <button onClick={() => toggleStep(index)} className="w-full flex items-center justify-between p-6 text-left focus:outline-none hover:bg-gray-50 transition-colors" data-id="element-542">
                    <div className="flex items-center" data-id="element-543">
                      <div className={`hidden md:flex flex-shrink-0 w-12 h-12 rounded-full items-center justify-center font-oswald text-xl font-bold mr-6 transition-colors ${isOpen ? 'bg-pts-red text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-544">
                        {index + 1}
                      </div>
                      <h3 className={`font-oswald text-xl font-bold uppercase transition-colors ${isOpen ? 'text-pts-red' : 'text-gray-900'}`} data-id="element-545">
                        <span className="md:hidden mr-2" data-id="element-546">{index + 1}.</span>
                        {step.title}
                      </h3>
                    </div>
                    <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform duration-300 ${isOpen ? 'transform rotate-180' : ''}`} data-id="element-547" />
                  </button>

                  <AnimatePresence data-id="element-548">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-549">
                        <div className="px-6 pb-6 md:pl-24 text-gray-600 leading-relaxed border-t border-gray-100 pt-4" data-id="element-550">
                          {step.content}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </div>
      </Container>
    </div>;
}